import { valibotResolver } from "@hookform/resolvers/valibot";
import clsx from "clsx";
import { useState } from "react";
import { useForm } from "react-hook-form";

import useWorkspaceSubscriptionMutations from "features/subscriptions/datahooks/useWorkspaceSubscriptionMutations";

import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import TextArea from "common/components/ui/TextArea";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import {
  CancelSubscriptionFormSchema,
  type CancelSubscriptionFormSchemaType,
} from "common/schemas";

type CancelStep = "confirm" | "feedback";

interface CancellationFlowProps {
  subscriptionId: string;
  mode: "skipSurvey" | "skipConfirmation" | "default";
  onCancellationSuccess: () => void;
  goBack: () => void;
  onSubStepChange?: (step: CancelStep) => void;
  avatarCount?: number;
}

export default function CancellationFlow({
  subscriptionId,
  mode = "default",
  onCancellationSuccess,
  goBack,
  onSubStepChange,
  avatarCount,
}: CancellationFlowProps) {
  const [step, setStep] = useState<CancelStep>(
    mode === "skipConfirmation" ? "feedback" : "confirm",
  );
  const isDesktop = useTwBreakpoint("md");

  const { cancelSubscription, isCancelingSubscription } =
    useWorkspaceSubscriptionMutations();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CancelSubscriptionFormSchemaType>({
    defaultValues: { reason: "" },
    resolver: valibotResolver(CancelSubscriptionFormSchema),
  });
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Header = isDesktop ? DialogHeader : DrawerHeader;
  const Body = isDesktop ? DialogBody : DrawerBody;

  async function onCancelSubscription(reason?: string) {
    await cancelSubscription({ subscriptionId, reason }).then(
      onCancellationSuccess,
    );
  }

  const onSubmit = async ({ reason }: CancelSubscriptionFormSchemaType) => {
    await onCancelSubscription(reason);
  };

  function goToFeedback() {
    setStep("feedback");
    onSubStepChange?.("feedback");
  }

  if (step === "confirm") {
    const isAvatarOrder = avatarCount != null;

    const title = isAvatarOrder
      ? `Cancel order for ${avatarCount} Avatar${avatarCount === 1 ? "" : "s"}?`
      : "Are you sure you want to cancel your subscription?";

    const description = isAvatarOrder
      ? avatarCount === 1
        ? "The Avatar will stay active until your next billing period. Once removed, this Avatar cannot be returned to your workspace."
        : "The Avatars will stay active until your next billing period. Once removed, this set of avatars cannot be returned to your workspace."
      : "This action can't be undone. All your campaigns activity will be cancelled and all seats will be unassigned";

    const confirmLabel = isAvatarOrder ? "Yes" : "Cancel subscription";

    return (
      <>
        <Header title={title} subtitle={description} />

        <Footer className="justify-between">
          <Button variant="secondary-black" onClick={goBack}>
            Back
          </Button>

          <Button
            variant="outline-danger"
            isLoading={isCancelingSubscription}
            onClick={
              mode === "skipSurvey"
                ? () => onCancelSubscription()
                : goToFeedback
            }
          >
            {confirmLabel}
          </Button>
        </Footer>
      </>
    );
  }

  return (
    <form
      className="flex flex-col"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <Header title="One more thing before you leave" />
      <Body>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="overflow-hidden md:w-[267px] w-full rounded-4xl">
            <iframe
              src="https://player.vimeo.com/video/1176509674?badge=0&autopause=0&autoplay=1&player_id=0&app_id=58479&title=0&byline=0&portrait=0"
              title="Alex Cancellation"
              height={281}
              width={isDesktop ? 158 : 328}
              allow="autoplay; fullscreen"
              className="bg-black-950 mx-auto w-full"
            />
          </div>

          <div className="flex flex-col gap-4 justify-between">
            <div className="flex flex-col gap-4 text-black-950 [&>p]:mb-0">
              <p className="text-md-medium">
                I'm sorry to see you go and would love to understand what led
                you to cancel.
              </p>

              <p className="text-md-bold">
                What can we do better in the future?
              </p>

              <div className="flex flex-col">
                <span className="text-sm font-semibold">Alex</span>

                <span className="text-black-800 text-sm-medium">
                  CEO Aimfox
                </span>
              </div>
            </div>

            <FormField
              label=""
              htmlFor="cancel-subscription-reason"
              className="relative"
              error={errors.reason?.message}
            >
              <TextArea
                id="cancel-subscription-reason"
                autoFocus
                minRows={3.5}
                maxRows={4}
                placeholder="Your feedback"
                {...register("reason")}
              />

              <span
                className={clsx([
                  "absolute right-2 text-xs",
                  watch("reason").length >= 2000
                    ? "text-red-500"
                    : "text-black-500",
                  errors.reason?.message ? "bottom-7" : "bottom-2",
                ])}
              >
                {watch("reason").length}/2000
              </span>
            </FormField>
          </div>
        </div>
      </Body>

      <Footer className="justify-between">
        <Button type="button" variant="secondary-black" onClick={goBack}>
          Keep my plan
        </Button>

        <Button type="submit" isLoading={isCancelingSubscription}>
          Cancel my subscription
        </Button>
      </Footer>
    </form>
  );
}
